/**
 * Модалка «Абонент» вкладки «Абоненты» (ecology-subscription, макет панели администратора 16.09 +
 * решение Р20): тариф, срок договора, инженер (имя, телефон, почта — текстом, это не аккаунт),
 * число выездов в год, ближайший выезд и что на нём будет, год плана работ. У нового абонента
 * выбирается клиент; у существующего клиент не меняется (сопровождение — одно на организацию).
 *
 * План работ правится отдельной модалкой: правка карточки не трогает отметки «выполнено».
 */
import { useEffect, useId, useState, type FormEvent } from 'react'
import {
  teamEcologySubscriptionsApi,
  type EcoSubscription,
  type EcoSubscriptionInput,
  type EcoTariff,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { ECO_TARIFF_LABELS } from '@/lib/ecology'
import { ClientSelect } from '@/company/client-select'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

const TARIFFS = Object.keys(ECO_TARIFF_LABELS) as EcoTariff[]

function emptyInput(): EcoSubscriptionInput {
  return {
    tariff: 'protection',
    contractUntil: '',
    engineerName: '',
    engineerPhone: '',
    engineerEmail: '',
    visitsPerYear: 2,
    nextVisitOn: '',
    nextVisitNote: '',
    planYear: new Date().getFullYear(),
    // 2-ТП (воздух) инженер оставляет всем абонентам, отчётность по воде включает по
    // необходимости (ответ заказчика 18.09).
    includesAirReport: true,
    includesWaterReport: false,
  }
}

function inputFrom(sub: EcoSubscription | null): EcoSubscriptionInput {
  if (!sub) return emptyInput()
  return {
    tariff: sub.tariff,
    contractUntil: sub.contractUntil,
    engineerName: sub.engineerName,
    engineerPhone: sub.engineerPhone,
    engineerEmail: sub.engineerEmail,
    visitsPerYear: sub.visitsPerYear,
    nextVisitOn: sub.nextVisitOn,
    nextVisitNote: sub.nextVisitNote,
    planYear: sub.planYear,
    includesAirReport: sub.includesAirReport,
    includesWaterReport: sub.includesWaterReport,
  }
}

export function SubscriberDialog({
  open,
  clientId,
  clientName,
  subscription,
  onClose,
  onSaved,
}: {
  open: boolean
  /** Клиент абонента; пусто — новый абонент, клиент выбирается в модалке. */
  clientId: string
  clientName: string
  /** Текущая карточка (для правки); null — новая. */
  subscription: EcoSubscription | null
  onClose: () => void
  onSaved: () => void
}) {
  const formId = useId()
  const [input, setInput] = useState<EcoSubscriptionInput>(() => inputFrom(subscription))
  const [client, setClient] = useState({ id: clientId, name: clientName })
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setInput(inputFrom(subscription))
    setClient({ id: clientId, name: clientName })
    setShowErrors(false)
    setError(null)
  }, [open, subscription, clientId, clientName])

  const patch = (next: Partial<EcoSubscriptionInput>) => setInput((r) => ({ ...r, ...next }))
  const clientError = showErrors && !client.id ? 'Выберите клиента' : undefined
  const yearError =
    showErrors && (input.planYear < 2020 || input.planYear > 2100)
      ? 'Укажите год плана работ'
      : undefined
  const title = subscription ? 'Сопровождение клиента' : 'Добавить абонента'

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    if (!client.id || input.planYear < 2020 || input.planYear > 2100) {
      setShowErrors(true)
      return
    }
    setError(null)
    setSaving(true)
    try {
      await teamEcologySubscriptionsApi.save(client.id, input)
      onSaved()
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить сопровождение'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onClose()
      }}
    >
      <DialogContent title={title} className="max-w-2xl">
        <Card
          scrollable
          className="max-h-dialog"
          header={title}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Отмена
              </Button>
              <Button type="submit" form={formId} disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          }
        >
          <form id={formId} onSubmit={submit} noValidate>
            {error ? (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            ) : null}

            {subscription ? (
              <p className="mb-3 font-medium text-txt">{client.name}</p>
            ) : (
              <Field label="Клиент" required error={clientError}>
                {(props) => (
                  <ClientSelect
                    id={props.id}
                    aria-label="Клиент"
                    value={client.id}
                    valueLabel={client.name}
                    onSelect={(c) => setClient({ id: c.id, name: c.name })}
                  />
                )}
              </Field>
            )}

            <div className="grid grid-cols-1 gap-x-4 nw:grid-cols-2">
              <Field label="Тариф" required>
                {(props) => (
                  <Select
                    value={input.tariff}
                    onValueChange={(v) => patch({ tariff: v as EcoTariff })}
                  >
                    <SelectTrigger id={props.id} aria-label="Тариф" className="text-left">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TARIFFS.map((t) => (
                        <SelectItem key={t} value={t}>
                          {ECO_TARIFF_LABELS[t]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Договор до">
                {(props) => (
                  <DateInput
                    {...props}
                    value={input.contractUntil}
                    onChange={(contractUntil) => patch({ contractUntil })}
                  />
                )}
              </Field>
            </div>

            <Field label="Инженер" hint="Имя видит клиент в блоке «Сопровождение»">
              {(props) => (
                <Input
                  {...props}
                  value={input.engineerName}
                  onChange={(e) => patch({ engineerName: e.target.value })}
                  placeholder="Ирина Ковалёва"
                />
              )}
            </Field>
            <div className="grid grid-cols-1 gap-x-4 nw:grid-cols-2">
              <Field label="Телефон инженера">
                {(props) => (
                  <Input
                    {...props}
                    value={input.engineerPhone}
                    onChange={(e) => patch({ engineerPhone: e.target.value })}
                    placeholder="+7 918 000-11-22"
                  />
                )}
              </Field>
              <Field label="Почта инженера">
                {(props) => (
                  <Input
                    {...props}
                    value={input.engineerEmail}
                    onChange={(e) => patch({ engineerEmail: e.target.value })}
                    placeholder="eco@centrika.ru"
                  />
                )}
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-x-4 nw:grid-cols-2">
              <Field label="Выездов в год">
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    min={0}
                    max={52}
                    value={String(input.visitsPerYear)}
                    onChange={(e) => patch({ visitsPerYear: Number(e.target.value) || 0 })}
                  />
                )}
              </Field>
              <Field label="Ближайший выезд">
                {(props) => (
                  <DateInput
                    {...props}
                    value={input.nextVisitOn}
                    onChange={(nextVisitOn) => patch({ nextVisitOn })}
                  />
                )}
              </Field>
            </div>
            <Field label="Что будет на выезде" hint="Клиент видит подписью к дате выезда">
              {(props) => (
                <Input
                  {...props}
                  value={input.nextVisitNote}
                  onChange={(e) => patch({ nextVisitNote: e.target.value })}
                  placeholder="инвентаризация мест накопления отходов"
                />
              )}
            </Field>
            <div className="mb-3 flex flex-col gap-2.5">
              <p className="text-13 font-medium text-txt">Входит в сопровождение</p>
              <LabeledCheckbox
                checked={input.includesAirReport}
                onCheckedChange={(includesAirReport) => patch({ includesAirReport })}
              >
                Отчёт 2-ТП (воздух)
              </LabeledCheckbox>
              <LabeledCheckbox
                checked={input.includesWaterReport}
                onCheckedChange={(includesWaterReport) => patch({ includesWaterReport })}
              >
                Отчётность по воде
              </LabeledCheckbox>
            </div>

            <Field label="Год плана работ" required error={yearError}>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={2020}
                  max={2100}
                  value={String(input.planYear)}
                  invalid={Boolean(yearError)}
                  onChange={(e) => patch({ planYear: Number(e.target.value) || 0 })}
                />
              )}
            </Field>
          </form>
        </Card>
      </DialogContent>
    </Dialog>
  )
}
